/**
 * AnnotationHighlight — colored underline + custom-tooltip for existing
 * annotations on a collab post.
 *
 * Strategy (Issue #15 rewrite):
 *   - Use the server's `start_pos`/`end_pos` (char-coords against the
 *     materialized text) as the AUTHORITATIVE anchor. Map char → PM
 *     position via mapCharToPm. The previous snippet-indexOf approach
 *     collapsed overlapping/identical annotations onto the first match
 *     and produced one shared tooltip for many distinct comments.
 *   - Snippet match is now a FALLBACK for the CRDT-drift case where the
 *     stored char positions no longer line up (other authors edited
 *     earlier in the doc).
 *   - Each decoration carries its own `data-annotation-id` and the
 *     full author-color so a sibling tooltip component (AnnotationTooltip)
 *     can render styled hover content keyed by id. The native `title`
 *     attribute is gone — Browser default tooltip was tiny and lost
 *     duplicate annotations to last-write-wins.
 *
 * Multiple annotations at the same range: separate decorations stack
 * naturally (ProseMirror renders nested spans). The tooltip component
 * walks parent spans on hover and shows all hit annotations grouped.
 *
 * Limitations:
 *   - True orphans (snippet gone from doc) are not rendered inline.
 *     Sidebar with orphaned list is a v2 feature.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view'
import { colorFromId } from './colorFromId'

const PLUGIN_KEY = new PluginKey<AnnotationState>('annotationHighlight')
const SET_ANNOTATIONS_META = 'annotationHighlight:setAnnotations'

export interface AnnotationReply {
  text: string
  author_id?: string | null
  author_name?: string | null
  created_at?: string | null
}

export interface AnnotationData {
  /** Either a DB-row number (REST annotations) or a section_id like
   *  "a-xxx" (section-derived annotations from annotationsFromSections). */
  id: number | string
  start_pos: number
  end_pos: number
  note_text: string
  author_id?: string | null
  author_name: string
  created_at?: string | null
  /** Text snippet captured at annotation-create time. Used only as
   *  CRDT-drift fallback when start_pos/end_pos no longer point at
   *  the intended text. */
  snippet?: string
  /** Marker for annotation origin: REST-created (db), section-derived
   *  (section), or other. Used for debug + drift-fallback decisions. */
  source?: 'section' | 'rest' | 'db'
  /** Reply thread for section-based annotations. Only populated when
   *  source='section'; legacy REST rows don't carry replies in v1. */
  replies?: AnnotationReply[]
}

interface AnnotationState {
  annotations: AnnotationData[]
}

interface AnnotationHighlightOptions {
  initial: AnnotationData[]
}

export const AnnotationHighlight = Extension.create<AnnotationHighlightOptions>({
  name: 'annotationHighlight',

  addOptions() {
    return { initial: [] }
  },

  addProseMirrorPlugins() {
    const initial = this.options.initial || []
    return [
      new Plugin<AnnotationState>({
        key: PLUGIN_KEY,
        state: {
          init: () => ({ annotations: initial }),
          apply: (tr, prev) => {
            const update = tr.getMeta(SET_ANNOTATIONS_META) as
              | AnnotationData[]
              | undefined
            if (update !== undefined) {
              return { annotations: update }
            }
            return prev
          },
        },
        props: {
          decorations(state) {
            const pluginState = PLUGIN_KEY.getState(state)
            if (!pluginState || pluginState.annotations.length === 0) {
              return DecorationSet.empty
            }
            const decorations: Decoration[] = []
            // Cache docText for snippet-fallback only — used rarely
            // once start_pos mapping is reliable.
            let docTextCache: string | null = null
            for (const ann of pluginState.annotations) {
              let from = mapCharToPm(state, ann.start_pos)
              let to = mapCharToPm(state, ann.end_pos)

              // CRDT-drift fallback: char positions invalid → search by
              // snippet. Only fires if start_pos no longer maps cleanly.
              if (from === null || to === null || to <= from) {
                const needle = (ann.snippet || '').trim()
                if (!needle) continue
                if (docTextCache === null) {
                  docTextCache = state.doc.textBetween(
                    0,
                    state.doc.content.size,
                    '\n'
                  )
                }
                const idx = docTextCache.indexOf(needle)
                if (idx < 0) continue
                from = mapCharToPm(state, idx)
                to = mapCharToPm(state, idx + needle.length)
                if (from === null || to === null || to <= from) continue
              }

              // Color stripe derived from author identity rather than
              // a single yellow — so multiple annotators are visually
              // distinguishable just like the AuthorHighlight per-
              // paragraph stripes. Falls back to amber when author_id
              // is missing (legacy or anon annotations).
              const idColor = ann.author_id
                ? colorFromId(ann.author_id, ann.author_name)
                : 'hsl(45, 90%, 50%)'

              decorations.push(
                Decoration.inline(from, to, {
                  // No `title` — see AnnotationTooltip component. The
                  // browser default tooltip is tiny + loses overlap.
                  class: 'annotation-highlight',
                  style:
                    `background: ${hslToRgba(idColor, 0.18)}; ` +
                    `border-bottom: 1.5px dotted ${idColor}; ` +
                    'cursor: help;',
                  'data-annotation-id': String(ann.id),
                  'data-author-color': idColor,
                })
              )
            }
            return DecorationSet.create(state.doc, decorations)
          },
        },
      }),
    ]
  },
})

/**
 * Walk PM doc, count chars matching the textBetween('\n') serialization,
 * return the PM pos for the given char index. Returns null if the index
 * is out of range. Mirrors `charPosFromPmPos` in AnnotationOverlay.ts
 * but in the inverse direction.
 */
function mapCharToPm(
  state: {
    doc: {
      descendants: (
        cb: (
          n: { isText: boolean; nodeSize: number; type: { name: string } },
          p: number
        ) => boolean
      ) => void
      content: { size: number }
    }
  },
  charIdx: number
): number | null {
  if (charIdx < 0) return null
  let charsSeen = 0
  let result: number | null = null
  let firstBlock = true
  state.doc.descendants((node, nodePos) => {
    if (result !== null) return false
    if (node.type.name === 'paragraph' || node.type.name === 'heading') {
      if (!firstBlock) {
        if (charsSeen === charIdx) {
          result = nodePos
          return false
        }
        charsSeen += 1
      }
      firstBlock = false
      return true
    }
    if (node.isText) {
      const len = node.nodeSize
      if (charsSeen + len >= charIdx) {
        result = nodePos + (charIdx - charsSeen)
        return false
      }
      charsSeen += len
    }
    return true
  })
  return result
}

/**
 * Parse an `hsl(h, s%, l%)` string and emit an rgba() with the supplied
 * alpha. Used to derive the soft annotation-background from the same
 * author color used for the underline so the highlight visually belongs
 * to the annotator without re-tinting at write-time.
 */
function hslToRgba(hsl: string, alpha: number): string {
  const m = /hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/.exec(hsl)
  if (!m) return `rgba(255, 230, 100, ${alpha})`
  const h = Number(m[1]) / 360
  const s = Number(m[2]) / 100
  const l = Number(m[3]) / 100
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255)
  const g = Math.round(hue2rgb(p, q, h) * 255)
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Set or replace the annotation list on an editor. Call this from React
 * effects that fetch /annotations/ and want to refresh the inline display.
 */
export function setAnnotations(
  editorView: EditorView,
  annotations: AnnotationData[]
) {
  const tr: Transaction = editorView.state.tr.setMeta(
    SET_ANNOTATIONS_META,
    annotations
  )
  editorView.dispatch(tr)
}
